import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

import AddToCart from '../../AddToCart';
import Benefits from '../Benefits/Benefits';
import Afterpay from '../../Afterpay';
import Options from '../Options';
import NumericInput from '../../NumericInput';
import Subscriptions from '../Subscriptions/Subscriptions';
import Button from '../../Button';

import { ProductFormContext } from '../../../context/product-form-context';
import { dataLayerPush } from '../../../utils';
import { COMING_SOON_LIST_ID } from '../../../utils/config';

const ns = `product-form`;

const Form = ({ variant, product, globalSettings }) => {
	const {
		isVariantAvailable,
		isRiseGiftCard,
	} = React.useContext(ProductFormContext);

	const [quantity, setQuantity] = useState(1);
	const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] =
		useState(null);
	const prevVariantTitle = useRef(null);
	const DISABLE_ADD_TO_CART_TAG = 'add-to-cart-hidden';
	const shouldDisableAddToCart = (product?.tags || []).includes(
		DISABLE_ADD_TO_CART_TAG
	);
	const comingSoonMetafieldValue = (product.metafields || []).find(metafield => { return metafield.key === COMING_SOON_LIST_ID })?.value;

	const handleQuantityChange = (value) => {
		if (value !== '' && Number(value) < 1) {
			return;
		}

		setQuantity(value);
	};

	function doIncrement() {
		handleQuantityChange(Number(quantity || 0) + 1);
	}

	function doDecrement() {
		handleQuantityChange(Number(quantity || 0) - 1);
	}

	const constructCustomAttributes = () => {
		const ret = [];
		const { metafields = [] } = product;
		const freeWithProduct =
			metafields.find((metafield) => {
				return metafield.key === 'free_with_product_id';
			})?.value || '';
		const productId = freeWithProduct.split('/').pop();

		if (productId) {
			ret.push({ key: '_free_with_product_id', value: productId });
		}

		if (selectedSubscriptionPlan) {
			ret.push({
				key: '_selling_plan_json',
				value: JSON.stringify(selectedSubscriptionPlan),
			});
		}

		return ret;
	};

	// const subscriptonCustomAttributes = (plan => {
	// We no longer need selling plan id in checkout
	// if (plan) {
	// 	return [
	// 		{
	// 			key: 'selling_plan',
	// 			value: `${ plan.external_plan_id }`,
	// 		},
	// 		{
	// 			key: '_selling_plan_json',
	// 			value: JSON.stringify(plan),
	// 		},
	// 	];
	// }

	// 	return [];
	// }) (selectedSubscriptionPlan);

	const constructSellingPlanId = (sellingPlanId) => {
		if (!sellingPlanId) {
			return null;
		}

		return `gid://shopify/SellingPlan/${sellingPlanId}`;
	};

	const onAddToCart = () => {
		dataLayerPush().addToCart({ product, variant, quantity });
	};

	useEffect(() => {
		// Create new "view item" GA event if user selects a different variant
		if (prevVariantTitle.current !== variant.title) {
			prevVariantTitle.current = variant.title;
			dataLayerPush().viewItem({ product, variant, quantity });
		}
	}, [product, quantity, variant]);

	const rootClassnames = classNames({
		[`${ns}`]: true,
	});

	return (
		<div className={rootClassnames}>
			<div className={`${ns}__option-groups`}>
				<Options />
			</div>
			{isVariantAvailable() && (
				<div className={`${ns}__subscriptions`}>
					<Subscriptions
						product={product}
						variant={variant}
						setSelectedSubscriptionPlan={setSelectedSubscriptionPlan}
						selectedSubscriptionPlan={selectedSubscriptionPlan}
						globalSettings={globalSettings}
					/>
				</div>
			)}
			<div
				className={classNames({
					[`${ns}__combined-button`]: true,
					[`${ns}__combined-button--with-margin`]: !isVariantAvailable(),
					'Rise-add-to-cart-button': isRiseGiftCard,
				})}
			>
				{shouldDisableAddToCart ? (
					<Button disabled>Coming Soon</Button>
				) : (
					<>
						{!comingSoonMetafieldValue && isVariantAvailable() && (
							<NumericInput
								value={quantity}
								aria-label={`Quantity`}
								onIncrement={() => {
									doIncrement();
								}}
								onDecrement={() => {
									doDecrement();
								}}
								onChange={(e) => {
									handleQuantityChange(e.currentTarget.value);
								}}
							/>
						)}
						<AddToCart
							variantId={variant ? variant.storefrontId : null}
							gqlProductId={product?.shopifyId || null}
							gqlVariantId={variant?.shopifyId || null}
							available={isVariantAvailable()}
							quantity={quantity}
							// sellingPlanId={selectedSubscriptionPlan?.external_product_id?.ecommerce}
							sellingPlanId={constructSellingPlanId(
								selectedSubscriptionPlan?.external_plan_id
							)}
							customAttributes={constructCustomAttributes()}
							handle={product.handle}
							onAddToCart={onAddToCart}
							metafields={product.metafields}
						/>
					</>
				)}
			</div>
			<Afterpay
				price={variant.price}
				currencyCode={product.priceRange.minVariantPrice.currencyCode}
			/>
			<div className={`${ns}__benefits`}>
				<Benefits product={product} globalSettings={globalSettings} />
			</div>
		</div>
	);
};

export default Form;
